import React, { useState, useEffect, useCallback } from 'react';
import {
  Select,
  Space,
  AutoComplete,
  Input,
  Typography,
  Tag,
  message
} from 'antd';
import {
  DatabaseOutlined,
  EditOutlined,
  CloseOutlined
} from '@ant-design/icons';
import api from '../services/api';

// 图片缓存和预加载工具
const imageCache = new Map<string, boolean>();
const imageDataCache = new Map<string, string>();

// 将图片转换为Base64缓存
const convertImageToBase64 = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (imageDataCache.has(src)) {
      resolve(imageDataCache.get(src)!);
      return;
    }
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        
        imageDataCache.set(src, dataURL);
        imageCache.set(src, true);
        resolve(dataURL);
      } catch (error) {
        imageCache.set(src, false);
        reject(error);
      }
    };
    
    img.onerror = () => {
      imageCache.set(src, false);
      reject(new Error('图片加载失败'));
    };
    
    img.src = src;
  });
};

const preloadImage = (src: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (imageCache.has(src)) {
      resolve(imageCache.get(src)!);
      return;
    }
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageCache.set(src, true);
      resolve(true);
    };
    img.onerror = () => {
      imageCache.set(src, false);
      resolve(false);
    };
    img.src = src;
  });
};

// 图片组件，带错误处理和默认图标
interface SafeImageProps {
  src: string;
  alt: string;
  size: number;
  defaultIcon: string;
  style?: React.CSSProperties;
}

const SafeImage: React.FC<SafeImageProps> = ({ src, alt, size, defaultIcon, style }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);

  // 尝试加载图片
  useEffect(() => {
    const loadImage = async () => {
      setIsLoading(true);
      setImageError(false);
      
      try {
        // 生成原始图片URL（去掉Fandom的图片处理参数）
        const originalSrc = src.replace(/\/revision\/latest\/scale-to-width-down\/\d+\?cb=\d+/, '');
        
        console.log('🔄 加载原始图片:', originalSrc);
        const success = await preloadImage(originalSrc);
        if (success) {
          console.log('✅ 原始图片加载成功:', originalSrc);
          setCurrentSrc(originalSrc);
          setImageLoaded(true);
        } else {
          console.log('❌ 原始图片加载失败:', originalSrc);
          setImageError(true);
        }
      } catch (error) {
        console.warn('图片加载异常:', src, error);
        setImageError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
  }, [src]);


  const handleError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setImageLoaded(true);
    setImageError(false);
    setIsLoading(false);
  };

  return (
    <div style={{ 
      width: size, 
      height: size, 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      borderRadius: 2,
      border: '1px solid #d9d9d9',
      ...style
    }}>
      {isLoading && !imageError && (
        <span style={{ 
          fontSize: size * 0.3, 
          color: '#ccc' 
        }}>
          ⏳
        </span>
      )}
      {!imageError && !isLoading && (
        <img 
          src={currentSrc} 
          alt={alt}
          style={{ 
            width: size - 4, 
            height: size - 4, 
            objectFit: 'contain',
            display: imageLoaded ? 'block' : 'none'
          }}
          onError={handleError}
          onLoad={handleLoad}
          crossOrigin="anonymous"
        />
      )}
      {imageError && (
        <span style={{ 
          fontSize: size * 0.5, 
          color: '#999' 
        }}>
          {defaultIcon}
        </span>
      )}
    </div>
  );
};

const { Option } = Select;
const { Text } = Typography;

// 物品分类
const ITEM_CATEGORIES = [
  'Resources',
  'Tools', 
  'Armor',
  'Saddles',
  'Structures',
  'Dye',
  'Consumables',
  'Recipes',
  'Eggs',
  'Farming',
  'Seeds',
  'Weapons and Attachments',
  'Ammunition',
  'Skins',
  'Chibi Pets',
  'Artifacts',
  'Trophies'
];

// 物品分类中文映射
const ITEM_CATEGORY_NAMES: Record<string, string> = {
  'Resources': '资源',
  'Tools': '工具',
  'Armor': '护甲',
  'Saddles': '鞍具',
  'Structures': '建筑',
  'Dye': '染料',
  'Consumables': '消耗品',
  'Recipes': '配方',
  'Eggs': '蛋类',
  'Farming': '农业',
  'Seeds': '种子',
  'Weapons and Attachments': '武器和配件',
  'Ammunition': '弹药',
  'Skins': '皮肤',
  'Chibi Pets': '奇比宠物',
  'Artifacts': '神器',
  'Trophies': '战利品'
};

// 地图分类
const MAP_CATEGORIES = [
  'The Island',
  'Scorched Earth',
  'Ragnarok',
  'Aberration',
  'Extinction',
  'Valguero',
  'Genesis: Part 1',
  'Crystal Isles',
  'Genesis: Part 2',
  'Lost Island'
];

// 地图分类中文映射
const MAP_CATEGORY_NAMES: Record<string, string> = {
  'The Island': '孤岛',
  'Scorched Earth': '焦土',
  'Ragnarok': '仙境',
  'Aberration': '畸变',
  'Extinction': '灭绝',
  'Valguero': '瓦尔盖罗',
  'Genesis: Part 1': '创世纪：第一部分',
  'Crystal Isles': '水晶岛',
  'Genesis: Part 2': '创世纪：第二部分',
  'Lost Island': '失落之岛'
};

interface ARKItem {
  id: number;
  name: string;
  category: string;
  blueprint_path: string;
  chinese_name: string;
  icon_url?: string;
  item_id?: string;
  class_name?: string;
}

interface ARKCreature {
  id: number;
  name: string;
  category: string;
  blueprint_path: string;
  chinese_name: string;
  icon_url?: string;
  entity_id?: string;
  name_tag?: string;
  map_name: string;
}

interface ARKItemSelectorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ARKItemSelector: React.FC<ARKItemSelectorProps> = ({
  value,
  onChange,
  placeholder = "选择物品或输入蓝图路径",
  disabled = false
}) => {
  // 选择器类型：'items' | 'creatures' | 'custom'
  const [selectorType, setSelectorType] = useState<'items' | 'creatures' | 'custom'>('items');
  
  // 物品相关状态
  const [items, setItems] = useState<ARKItem[]>([]);
  const [selectedItemCategory, setSelectedItemCategory] = useState<string>('');
  const [filteredItems, setFilteredItems] = useState<ARKItem[]>([]);
  const [itemSearchValue, setItemSearchValue] = useState<string>('');
  
  // 生物相关状态
  const [creatures, setCreatures] = useState<ARKCreature[]>([]);
  const [selectedMap, setSelectedMap] = useState<string>('');
  const [filteredCreatures, setFilteredCreatures] = useState<ARKCreature[]>([]);
  const [creatureSearchValue, setCreatureSearchValue] = useState<string>('');
  
  // 自定义输入
  const [customBlueprint, setCustomBlueprint] = useState<string>('');
  
  // 当前选择的物品/生物信息
  const [selectedItem, setSelectedItem] = useState<ARKItem | null>(null);
  const [selectedCreature, setSelectedCreature] = useState<ARKCreature | null>(null);
  
  // 加载状态
  const [itemsLoading, setItemsLoading] = useState<boolean>(false);
  const [creaturesLoading, setCreaturesLoading] = useState<boolean>(false);
  

  // 加载物品数据
  const loadItems = useCallback(async (category?: string) => {
    setItemsLoading(true);
    try {
      const response = await api.get('/ark-rcon/items', {
        params: { category }
      });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('加载物品数据失败:', error);
      message.error('加载物品数据失败');
    } finally {
      setItemsLoading(false);
    }
  }, []);

  // 加载生物数据
  const loadCreatures = useCallback(async (mapName?: string) => {
    setCreaturesLoading(true);
    try {
      const response = await api.get('/ark-rcon/creatures', {
        params: { map_name: mapName }
      });
      setCreatures(response.data.creatures || []);
    } catch (error) {
      console.error('加载生物数据失败:', error);
      message.error('加载生物数据失败');
    } finally {
      setCreaturesLoading(false);
    }
  }, []);

  // 处理选择器类型变化
  const handleSelectorTypeChange = (type: 'items' | 'creatures' | 'custom') => {
    setSelectorType(type);
    setSelectedItemCategory('');
    setSelectedMap('');
    setItemSearchValue('');
    setCreatureSearchValue('');
    setCustomBlueprint('');
    setSelectedItem(null);
    setSelectedCreature(null);
    
    if (type === 'items' && items.length === 0) {
      loadItems();
    } else if (type === 'creatures' && creatures.length === 0) {
      loadCreatures();
    }
    
    // 清空当前值
    onChange?.('');
  };

  // 处理物品分类变化
  const handleItemCategoryChange = (category: string) => {
    setSelectedItemCategory(category);
    setItemSearchValue('');
    loadItems(category);
  };

  // 处理地图变化
  const handleMapChange = (mapName: string) => {
    setSelectedMap(mapName);
    setCreatureSearchValue('');
    loadCreatures(mapName);
  };

  // 处理物品搜索
  const handleItemSearch = useCallback((searchValue: string) => {
    setItemSearchValue(searchValue);
    if (!searchValue) {
      setFilteredItems(items);
      return;
    }
    
    const filtered = items.filter(item => 
      item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.chinese_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.blueprint_path.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredItems(filtered);
  }, [items]);

  // 处理生物搜索
  const handleCreatureSearch = useCallback((searchValue: string) => {
    setCreatureSearchValue(searchValue);
    if (!searchValue) {
      setFilteredCreatures(creatures);
      return;
    }
    
    const filtered = creatures.filter(creature => 
      creature.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      creature.chinese_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      creature.blueprint_path.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredCreatures(filtered);
  }, [creatures]);

  // 物品和生物选择处理已移除，通过AutoComplete的onSelect处理

  // 处理自定义输入
  const handleCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomBlueprint(value);
    setSelectedItem(null);
    setSelectedCreature(null);
    onChange?.(value);
  };

  // 清空当前选择
  const handleClearSelection = () => {
    setSelectedItem(null);
    setSelectedCreature(null);
    setItemSearchValue('');
    setCreatureSearchValue('');
    setCustomBlueprint('');
    onChange?.('');
    message.success('已清空选择');
  };


  // 初始化时加载物品数据
  useEffect(() => {
    if (selectorType === 'items' && items.length === 0) {
      loadItems();
    }
  }, [selectorType, items.length, loadItems]);

  // 更新过滤后的物品列表
  useEffect(() => {
    if (itemSearchValue) {
      handleItemSearch(itemSearchValue);
    } else {
      setFilteredItems(items);
    }
  }, [items, itemSearchValue, handleItemSearch]);

  // 更新过滤后的生物列表
  useEffect(() => {
    if (creatureSearchValue) {
      handleCreatureSearch(creatureSearchValue);
    } else {
      setFilteredCreatures(creatures);
    }
  }, [creatures, creatureSearchValue, handleCreatureSearch]);

  return (
    <div style={{ width: '100%' }}>
      {/* 选择器类型 */}
      <div style={{ marginBottom: 16 }}>
        <Select
          value={selectorType}
          onChange={handleSelectorTypeChange}
          style={{ width: '100%' }}
          disabled={disabled}
        >
          <Option value="items">
            <Space>
              <DatabaseOutlined />
              从物品库中获取
            </Space>
          </Option>
          <Option value="creatures">
            <Space>
              <DatabaseOutlined />
              从生物库中获取
            </Space>
          </Option>
          <Option value="custom">
            <Space>
              <EditOutlined />
              自定义输入蓝图代码
            </Space>
          </Option>
        </Select>
      </div>

      {/* 物品选择器 */}
      {selectorType === 'items' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* 物品分类选择 */}
          <Select
            placeholder="选择物品分类"
            value={selectedItemCategory}
            onChange={handleItemCategoryChange}
            style={{ width: '100%' }}
            disabled={disabled}
            loading={itemsLoading}
          >
            <Option value="">全部分类</Option>
            {ITEM_CATEGORIES.map(category => (
              <Option key={category} value={category}>
                {ITEM_CATEGORY_NAMES[category]} ({category})
              </Option>
            ))}
          </Select>

          {/* 物品搜索 */}
          <AutoComplete
            placeholder="搜索物品名称或蓝图路径"
            value={itemSearchValue}
            onChange={handleItemSearch}
            style={{ width: '100%' }}
            disabled={disabled}
            options={filteredItems.map(item => ({
              value: item.blueprint_path,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {item.icon_url && (
                    <SafeImage
                      src={item.icon_url}
                      alt={item.chinese_name || item.name}
                      size={50}
                      defaultIcon="📦"
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {item.chinese_name || item.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {item.name} | {item.blueprint_path}
                    </div>
                    {item.category && (
                      <Tag style={{ marginTop: 4 }}>
                        {ITEM_CATEGORY_NAMES[item.category] || item.category}
                      </Tag>
                    )}
                  </div>
                </div>
              )
            }))}
            onSelect={(value) => {
              const item = filteredItems.find(item => item.blueprint_path === value);
              setSelectedItem(item || null);
              setSelectedCreature(null);
              onChange?.(value);
            }}
          />

          {/* 物品列表已移除，避免加载过多数据导致卡顿 */}
        </Space>
      )}

      {/* 生物选择器 */}
      {selectorType === 'creatures' && (
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* 地图选择 */}
          <Select
            placeholder="选择地图"
            value={selectedMap}
            onChange={handleMapChange}
            style={{ width: '100%' }}
            disabled={disabled}
            loading={creaturesLoading}
          >
            <Option value="">全部地图</Option>
            {MAP_CATEGORIES.map(mapName => (
              <Option key={mapName} value={mapName}>
                {MAP_CATEGORY_NAMES[mapName]} ({mapName})
              </Option>
            ))}
          </Select>

          {/* 生物搜索 */}
          <AutoComplete
            placeholder="搜索生物名称或蓝图路径"
            value={creatureSearchValue}
            onChange={handleCreatureSearch}
            style={{ width: '100%' }}
            disabled={disabled}
            options={filteredCreatures.map(creature => ({
              value: creature.blueprint_path,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {creature.icon_url && (
                    <SafeImage
                      src={creature.icon_url}
                      alt={creature.chinese_name || creature.name}
                      size={50}
                      defaultIcon="🦕"
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {creature.chinese_name || creature.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {creature.name} | {creature.blueprint_path}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {creature.map_name && (
                        <Tag color="blue">
                          {MAP_CATEGORY_NAMES[creature.map_name] || creature.map_name}
                        </Tag>
                      )}
                      {creature.category && (
                        <Tag>
                          {creature.category}
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              )
            }))}
            onSelect={(value) => {
              const creature = filteredCreatures.find(creature => creature.blueprint_path === value);
              setSelectedCreature(creature || null);
              setSelectedItem(null);
              onChange?.(value);
            }}
          />

          {/* 生物列表已移除，避免加载过多数据导致卡顿 */}
        </Space>
      )}

      {/* 自定义输入 */}
      {selectorType === 'custom' && (
        <Input
          placeholder="请输入蓝图路径，例如：Blueprint'/Game/PrimalEarth/CoreBlueprints/Resources/PrimalItemResource_Stone.PrimalItemResource_Stone'"
          value={customBlueprint}
          onChange={handleCustomInputChange}
          disabled={disabled}
          style={{ width: '100%' }}
        />
      )}

      {/* 当前选择的值显示 */}
      {value && (
        <div style={{ 
          marginTop: 12, 
          padding: 12, 
          backgroundColor: '#f6ffed', 
          border: '1px solid #b7eb8f',
          borderRadius: 6,
          fontSize: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong>当前选择：</Text>
            <button
              onClick={handleClearSelection}
              style={{
                padding: '2px 6px',
                fontSize: '12px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                color: '#666',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
              title="清空选择"
            >
              <CloseOutlined />
              清空
            </button>
          </div>
          
          {/* 第一行：预览图片和信息 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            {(selectedItem || selectedCreature) && (
              <>
                {(selectedItem?.icon_url || selectedCreature?.icon_url) && (
                  <SafeImage
                    src={selectedItem?.icon_url || selectedCreature?.icon_url || ''}
                    alt={selectedItem?.chinese_name || selectedItem?.name || selectedCreature?.chinese_name || selectedCreature?.name || ''}
                    size={60}
                    defaultIcon={selectedItem ? '📦' : '🦕'}
                    style={{ borderRadius: 4 }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: 4 }}>
                    {selectedItem?.chinese_name || selectedItem?.name || selectedCreature?.chinese_name || selectedCreature?.name}
                  </div>
                  <div style={{ color: '#666', marginBottom: 4 }}>
                    {selectedItem?.name || selectedCreature?.name}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {selectedItem?.category && (
                      <Tag>{ITEM_CATEGORY_NAMES[selectedItem.category] || selectedItem.category}</Tag>
                    )}
                    {selectedCreature?.map_name && (
                      <Tag color="blue">{MAP_CATEGORY_NAMES[selectedCreature.map_name] || selectedCreature.map_name}</Tag>
                    )}
                    {selectedCreature?.category && (
                      <Tag>{selectedCreature.category}</Tag>
                    )}
                  </div>
                </div>
              </>
            )}
            {!selectedItem && !selectedCreature && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ 
                  width: 60, 
                  height: 60, 
                  backgroundColor: '#f5f5f5', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  color: '#999'
                }}>
                  📝
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: 4 }}>
                    自定义蓝图路径
                  </div>
                  <div style={{ color: '#666' }}>
                    手动输入的蓝图代码
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* 第二行：蓝图路径 */}
          <div style={{ 
            padding: '8px 12px', 
            backgroundColor: '#fafafa', 
            border: '1px solid #e8e8e8', 
            borderRadius: 4,
            wordBreak: 'break-all', 
            color: '#666',
            fontSize: '11px',
            fontFamily: 'monospace'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#333' }}>蓝图路径：</div>
            {value}
          </div>
        </div>
      )}
    </div>
  );
};

export default ARKItemSelector;
